import { useEffect } from "react";

/**
 * SEO-managed <head> sync for SPA route changes.
 *
 * Why a custom hook instead of react-helmet-async?
 *   Helmet bundles +~10KB and isn't needed: we already have a single root,
 *   and direct DOM mutation is fine since search crawlers run JS and read
 *   the post-mutation head. We tag every managed element with
 *   data-seo-managed="true" so we can clean up safely without disturbing
 *   static head children (favicon, manifest, etc.) that ship in index.html.
 */

/** Base URL for SEO (og:image, canonical). Must be absolute. */
const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL)
  || (typeof window !== "undefined" ? window.location.origin : "https://lenz3.com");

/** Languages we support and emit hreflang alternates for. Must match i18n SUPPORTED_LANGUAGES. */
const SUPPORTED_LANGS = ['fa', 'en', 'ar'] as const;
const LOCALE_MAP: Record<string, string> = { fa: 'fa_IR', en: 'en_US', ar: 'ar_AR' };

interface HeadPropsType {
  title?: string;
  description?: string;
  noIndex?: boolean;
  /** Canonical URL for this page (absolute). If not set, falls back to current URL with query/hash stripped. */
  canonical?: string;
  /** Image URL for og:image and twitter:image (e.g. post image). Must be absolute for social previews. */
  image?: string;
  /** Optional descriptive alt for the social image; defaults to page title. */
  imageAlt?: string;
  /** Open Graph type; use `article` for posts, `profile` for user profiles. Default: website */
  ogType?: 'website' | 'article' | 'profile';
  /** When ogType is `profile`, sets `profile:username` for Facebook/OG parsers */
  profileUsername?: string;
  /** When ogType is `article`, ISO timestamps for article meta. */
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  /** Optional Twitter @handle (without the @). */
  twitterSite?: string;
  /** JSON-LD document. Replaced on each route so stale entity schema does not leak. */
  jsonLd?: string;
}

const DEFAULT_TITLE = "لنزسه، مرجع نقل‌وانتقالات — آخرین جابه‌جایی‌ها را ببین، با هواداران حرف بزن";
const DEFAULT_DESCRIPTION = "لنزسه، مرجع نقل‌وانتقالات (لنز سه / Lenz3) — آخرین جابه‌جایی بازیکنان، باشگاه‌ها و لیگ‌ها را ببین و با هواداران حرف بزن.";
const SEO_FLAG = 'data-seo-managed';

/** Remove old SEO-managed nodes by selector so we don't accumulate stale meta on route changes. */
function purgeManaged(selector: string) {
  document.querySelectorAll(`${selector}[${SEO_FLAG}="true"]`).forEach((el) => el.remove());
}

/** Upsert an attribute-keyed meta tag and tag it as SEO-managed. */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(SEO_FLAG, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Upsert canonical/alternate <link rel="..."> with optional hreflang. */
function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    el.setAttribute(SEO_FLAG, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Resolve a possibly-relative image path to an absolute URL crawlers can fetch. */
function toAbsoluteImage(image?: string): string {
  if (!image) return `${SITE_URL}/logo.png`;
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
}

/** Strip query/hash to get a stable canonical when the page didn't provide one. */
function defaultCanonical(): string {
  if (typeof window === 'undefined') return SITE_URL;
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
}

const Head = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  noIndex = false,
  canonical,
  image,
  imageAlt,
  ogType = 'website',
  profileUsername,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  twitterSite,
  jsonLd,
}: HeadPropsType) => {
  useEffect(() => {
    document.title = title;

    setMeta('name', 'description', description);

    const canonicalUrl = canonical || defaultCanonical();
    setLink('canonical', canonicalUrl);

    // hreflang alternates: same URL for all languages because the SPA
    // serves identical HTML; this still tells Google the page is available
    // in each language and avoids duplicate content penalties.
    // Remove ALL existing alternates (static from index.html or stale dynamic)
    // before adding fresh ones for the current canonical, otherwise stale
    // hreflangs from the previous route would point to the old page.
    document.querySelectorAll('link[rel="alternate"]').forEach((el) => el.remove());
    SUPPORTED_LANGS.forEach((lng) => setLink('alternate', canonicalUrl, lng));
    setLink('alternate', canonicalUrl, 'x-default');

    // robots: explicit index vs noindex avoids stale noindex sticking around
    // on the next route, which would silently de-index the entire SPA.
    setMeta(
      'name',
      'robots',
      noIndex
        ? 'noindex,nofollow'
        : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    );
    setMeta(
      'name',
      'googlebot',
      noIndex
        ? 'noindex,nofollow'
        : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    );

    // Open Graph
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:site_name', 'لنزسه');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonicalUrl);

    const imageUrl = toAbsoluteImage(image);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:image:alt', imageAlt || title);
    setMeta('property', 'og:image:width', '512');
    setMeta('property', 'og:image:height', '512');

    const docLang = (document.documentElement?.lang || 'fa').toLowerCase();
    const primaryLocale = LOCALE_MAP[docLang] || LOCALE_MAP.fa;
    setMeta('property', 'og:locale', primaryLocale);

    // Alternate locales: list all supported except the active one
    purgeManaged('meta[property="og:locale:alternate"]');
    SUPPORTED_LANGS.filter((l) => l !== docLang).forEach((l) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.setAttribute('content', LOCALE_MAP[l]);
      meta.setAttribute(SEO_FLAG, 'true');
      document.head.appendChild(meta);
    });

    // Article-specific OG (only meaningful when ogType === 'article')
    if (ogType === 'article') {
      if (articlePublishedTime) setMeta('property', 'article:published_time', articlePublishedTime);
      if (articleModifiedTime) setMeta('property', 'article:modified_time', articleModifiedTime);
      if (articleAuthor) setMeta('property', 'article:author', articleAuthor);
    } else {
      document.querySelector('meta[property="article:published_time"]')?.remove();
      document.querySelector('meta[property="article:modified_time"]')?.remove();
      document.querySelector('meta[property="article:author"]')?.remove();
    }

    if (ogType === 'profile' && profileUsername) {
      setMeta('property', 'profile:username', profileUsername);
    } else {
      document.querySelector('meta[property="profile:username"]')?.remove();
    }

    // Twitter: MUST use name=, not property=. Strip any legacy property= variants.
    document.querySelectorAll('meta[property^="twitter:"]').forEach((el) => el.remove());
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);
    setMeta('name', 'twitter:image:alt', imageAlt || title);
    if (twitterSite) setMeta('name', 'twitter:site', twitterSite.startsWith('@') ? twitterSite : `@${twitterSite}`);

    const ldId = 'seo-jsonld';
    document.getElementById(ldId)?.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.id = ldId;
      script.type = 'application/ld+json';
      script.setAttribute(SEO_FLAG, 'true');
      script.textContent = jsonLd;
      document.head.appendChild(script);
    }
  }, [
    title,
    description,
    noIndex,
    canonical,
    image,
    imageAlt,
    ogType,
    profileUsername,
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor,
    twitterSite,
    jsonLd,
  ]);

  return null;
};

export default Head;
